import React, { useMemo, useRef, useState } from "react";
import { View, StyleSheet } from "react-native";
import NtListHeader from "../../components/NtListHeader";
import NtListRow from "../../components/NtListRow";
import NtModal from "../../components/NtModal";
import NtModalError from "../../components/NtModalError";
import NtListEmpty from "../../components/NtListEmpty";
import NtActivityIndicator from "../../components/NtActivityIndicator";
import NtModalGroupItemsAddButton from "../../components/NtModalGroupItemsAddButton";
import NtModalInputTitle from "../../components/NtModalInputTitle";
import NtIvrOptionForm from "./NtIvrOptionForm";
import NtAccountDisplay from "./NtAccountDisplay";
import NtListActionButton from "../../components/NtListActionButton";
import { parseError } from "@ntconnect/framework/pbx";
import { useTheme } from "../../context/ThemeContext";
import ivroptioncsvcolumndata from "../data/ivroptioncsvcolumndata";

/**
 * Inline IVR options editor — mirrors NtModalRingGroupDestinationsField.
 * Purely presentational. The parent owns the `options` array and decides
 * inside `handleCreateRequest` / `handleDeleteRequest` whether to call the
 * API (edit mode) or just update local state (new-menu mode).
 */
function NtModalIvrOptionsField({
  title,
  containerStyle,
  options,
  onCreate,
  handleCreateRequest,
  onDelete,
  handleDeleteRequest,
  handleUpdateRequest,
  loading = false,
}) {
  const { theme } = useTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  const newOptionRef = useRef(null);
  const editOptionRef = useRef(null);

  const toRow = (opt, idx) => ({
    digits: opt?.digits || opt?.ivr_menu_option_digits,
    description: opt?.description || opt?.ivr_menu_option_description,
    action: opt?.action?.name || opt?.ivr_menu_option_action,
    value: opt?.action?.value || opt?.ivr_menu_option_data,
    _key: opt?.id || opt?.ivr_menu_option_uuid || `${opt?.digits}-${idx}`,
    _raw: opt,
  });

  const rows = (options || []).map(toRow);

  const handleDelete = async (row) => {
    if (!onDelete) return;
    if (handleDeleteRequest) {
      setError(null);
      setDeleting(row._key);
      const response = await handleDeleteRequest(row._raw);
      if (response.ok) {
        onDelete(row._raw);
      } else {
        setError(parseError(response));
      }
      setDeleting(null);
    } else {
      onDelete(row._raw);
    }
  };

  const renderNewOptionModal = () => (
    <NtModal ref={newOptionRef}>
      <NtIvrOptionForm
        draft
        onCancel={() => newOptionRef.current?.dismiss()}
        onSave={async (params) => {
          if (handleCreateRequest) {
            const response = await handleCreateRequest(params);
            if (response.ok) {
              newOptionRef.current?.dismiss();
              if (onCreate) onCreate(params);
            } else {
              setError(parseError(response));
            }
          } else if (onCreate) {
            newOptionRef.current?.dismiss();
            onCreate(params);
          }
        }}
      />
    </NtModal>
  );

  const renderEditOptionModal = () => (
    <NtModal ref={editOptionRef}>
      <NtIvrOptionForm
        draft
        item={
          editingItem
            ? {
                // Map our row/raw shape into the props NtIvrOptionForm reads
                // when prefilling for edit.
                digits:
                  editingItem.ivr_menu_option_digits || editingItem.digits,
                description:
                  editingItem.ivr_menu_option_description ||
                  editingItem.description ||
                  "",
                order:
                  editingItem.ivr_menu_option_order || editingItem.order || 0,
                action: editingItem.action
                  ? editingItem.action
                  : {
                      name: editingItem.ivr_menu_option_action,
                      value: editingItem.ivr_menu_option_data,
                    },
              }
            : null
        }
        onCancel={() => editOptionRef.current?.dismiss()}
        onSave={async (params) => {
          if (handleUpdateRequest) {
            const response = await handleUpdateRequest(editingItem, params);
            if (response?.ok) {
              editOptionRef.current?.dismiss();
            } else {
              setError(parseError(response));
            }
          } else {
            editOptionRef.current?.dismiss();
          }
        }}
      />
    </NtModal>
  );

  return (
    <View style={[styles.container, containerStyle]}>
      <NtModalInputTitle style={{ marginLeft: 4, marginBottom: 4 }}>
        {title}
      </NtModalInputTitle>

      <View style={styles.box}>
        <NtListHeader
          headerContainerStyle={{ marginRight: 35 }}
          selectedColumns={ivroptioncsvcolumndata}
        />

        <NtListEmpty message="No options" visible={rows.length === 0} />

        {rows.map((row) => (
          <NtListRow
            key={row._key}
            item={row}
            columns={ivroptioncsvcolumndata}
            renderCustom={(item, columnId) => {
              if (columnId === "destination" && item?.action) {
                return (
                  <NtAccountDisplay
                    item={{ name: item.action, value: item.value }}
                  />
                );
              }
              return undefined;
            }}
            renderLeadingAction={
              handleUpdateRequest ? (
                <NtListActionButton
                  icon="pencil"
                  color={theme.brand2}
                  onPress={() => {
                    setEditingItem(row._raw);
                    editOptionRef.current?.show();
                  }}
                />
              ) : null
            }
            onActionIcon="delete"
            onActionColor={theme.red}
            onActionLoading={deleting === row._key}
            onActionPress={() => handleDelete(row)}
          />
        ))}

        <NtModalError error={error} containerStyle={{ marginTop: 8 }} />

        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <NtModalGroupItemsAddButton
            containerStyle={{ marginTop: 10, marginBottom: 10 }}
            title="Add option"
            onPress={() => newOptionRef.current?.show()}
          />

          {loading && (
            <NtActivityIndicator
              color={theme.brand}
              containerStyle={{ marginLeft: 10 }}
              size="small"
            />
          )}
        </View>
      </View>
      {renderNewOptionModal()}
      {renderEditOptionModal()}
    </View>
  );
}

const makeStyles = (t) =>
  StyleSheet.create({
    container: { marginBottom: 8 },
    box: {
      paddingHorizontal: 12,
      backgroundColor: t.surface2,
      borderWidth: 1,
      borderColor: t.borderSoft,
      borderRadius: t.radius,
    },
  });

export default NtModalIvrOptionsField;
